import { Component, ReactNode } from 'react'
import { AlertTriangle, RotateCcw, Home } from 'lucide-react'

type Props = {
  children: ReactNode
  /** با تغییر این مقدار، مرز خطا خودش را بازنشانی می‌کند (معمولاً مسیر صفحه). */
  resetKey?: string
  onReset?: () => void
  /** پوسته‌ای که پیام خطا داخل آن نمایش داده می‌شود — تا منو و ناوبری بماند. */
  frame?: (node: ReactNode) => ReactNode
}
type State = { error: Error | null; where: string }

/**
 * مرز خطا
 * =======
 * بدون این کامپوننت، یک استثنای ساده در رندر باعث می‌شود ری‌اکت کل درخت را
 * پیاده کند و کاربر صفحه‌ای کاملاً خالی ببیند.
 *
 * دو درسِ گران‌قیمت که در این نسخه لحاظ شده:
 *
 * ۱. پیام خطا **نباید** جای کل صفحه را بگیرد. اگر منو و ناوبری هم ناپدید شود،
 *    کاربر هیچ راهی جز رفرش کامل ندارد — دقیقاً همان چیزی که قرار بود حل شود.
 *    پس خطا داخل همان پوسته سایت (`frame`) نمایش داده می‌شود تا کاربر بتواند
 *    با یک کلیک به بخش دیگری برود.
 *
 * ۲. بازنشانی با `resetKey` انجام می‌شود، نه با `key` روی خود کامپوننت. با
 *    `key`، هر جابه‌جایی کل درخت — از جمله نوار بالا، اعلان‌ها و اتصال زنده —
 *    را پیاده و دوباره سوار می‌کرد.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, where: '' }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('[ارزینو] خطای رندر:', error, info)
    // نام کامپوننت‌هایی که خطا از درونشان آمده — بدون این، پیام خطا مثل
    // «Cannot read properties of undefined» می‌گوید *چه* شد ولی نه *کجا*.
    const stack = (info?.componentStack || '').trim().split('\n')
      .map((l) => l.trim()).filter(Boolean).slice(0, 6).join('\n')
    this.setState({ where: stack })
  }

  componentDidUpdate(prev: Props) {
    // کاربر به صفحه دیگری رفت ⇒ خطای صفحه قبلی دیگر موضوعیت ندارد
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, where: '' })
    }
  }

  reset = () => {
    this.setState({ error: null, where: '' })
    this.props.onReset?.()
  }

  render() {
    if (!this.state.error) return this.props.children

    const body = (
      <div className="grid min-h-[60vh] place-items-center p-6">
        <div className="card max-w-lg p-8 text-center">
          <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl border border-down/30 bg-down/10 text-down">
            <AlertTriangle className="h-7 w-7" />
          </span>
          <h2 className="mb-2 text-lg font-bold text-white">این بخش بالا نیامد</h2>
          <p className="mb-5 text-sm leading-7 text-slate-400">
            هنگام نمایش این صفحه خطایی رخ داد. می‌توانید دوباره تلاش کنید یا از
            منو به بخش دیگری بروید؛ اطلاعات حساب شما دست‌نخورده باقی مانده است.
          </p>

          {/* جزئیات عمداً **باز** است.
              وقتی این صفحه بالا می‌آید، تنها سرنخ موجود همین متن است. اگر پشت
              یک «جزئیات فنی» بسته پنهان بماند، گزارشی که به پشتیبانی می‌رسد
              فقط «کار نمی‌کند» است و پیدا‌کردن علت به حدس تبدیل می‌شود. */}
          <div className="mb-3 text-right">
            <div className="mb-1.5 text-xs text-slate-500">
              متن خطا — لطفاً همین را برای پشتیبانی بفرستید:
            </div>
            {this.props.resetKey && (
              <div className="num mb-1.5 text-[11px] text-slate-500" dir="ltr">
                صفحه: {this.props.resetKey}
              </div>
            )}
            <pre className="num max-h-40 select-all overflow-auto rounded-xl border border-white/[.06] bg-ink-900/70 p-3 text-left text-[11px] leading-6 text-slate-300"
              dir="ltr">
              {this.state.error.message || String(this.state.error)}
            </pre>
            {this.state.where && (
              <pre className="num mt-1.5 max-h-24 select-all overflow-auto rounded-xl border border-white/[.06] bg-ink-900/70 p-3 text-left text-[10px] leading-5 text-slate-500"
                dir="ltr">
                {this.state.where}
              </pre>
            )}
          </div>

          <button
            onClick={() => navigator.clipboard?.writeText(
              [this.props.resetKey, this.state.error?.message, this.state.where]
                .filter(Boolean).join('\n'))}
            className="mb-5 text-xs text-brand-300 hover:underline">
            کپی متن خطا
          </button>

          <div className="flex flex-wrap justify-center gap-2">
            <button onClick={this.reset} className="btn-primary !px-4 !py-2 text-sm">
              <RotateCcw className="h-4 w-4" /> تلاش دوباره
            </button>
            <a href="/" className="btn-ghost !px-4 !py-2 text-sm">
              <Home className="h-4 w-4" /> صفحه اصلی
            </a>
          </div>
        </div>
      </div>
    )

    return this.props.frame ? <>{this.props.frame(body)}</> : body
  }
}

export default ErrorBoundary
